import { Component, Input, ElementRef, ViewChild, Output, EventEmitter, SimpleChanges, OnInit, OnChanges, AfterViewInit, OnDestroy } from '@angular/core';
import { NgForm, AbstractControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
	selector: 'bb-form-element',
	templateUrl: './bb-form-element.component.html',
	styleUrls: ['./bb-form-element.component.scss']
})

export class BbFormElementComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@Input() label: string;
	@Input() labelIcon: string;
	@Input() hideLabel: boolean;
	@Input() required: boolean;
	@Input() hideRequiredMark: boolean;
	@Input() disabled: boolean;
	@Input() postLabel: string;
	@Input() infoMessage: string;
	@Input() helpMessage: string;
	@Input() helpMessageLink: string;
	@Input() control: AbstractControl;
	@Input() form: NgForm;
	@Input() actionButtonIcon: string;
	@Input() actionButtonDisabled: boolean;
	@Input() theme: '' | 'light-v2' = '';
	@Output() actionButtonClick = new EventEmitter<void>();

	@ViewChild('inputWrapper', { static: true }) inputWrapper: ElementRef;

	inputElement: any; // the actual form element (or custom element)
	formControl: AbstractControl;
	exceptions: boolean;
	numCheckboxes = 0;

	formControlName: string;
	formControlChangedSubscription: Subscription;

	constructor() {
		this.exceptions = false;
	}

	ngOnInit() {
		this.determineInputElement();
		if (this.inputElement) {
			if (typeof this.required === 'undefined') {
				this.required = (this.inputElement.getAttribute('required') !== null);
			} else if (this.required) {
				this.inputElement.setAttribute('required', '');
			}
			if (this.form) {
				this.formControlName = this.inputElement.getAttribute('name');
				this.formControl = this.form.form.controls[this.formControlName];
			}
		}
		if (this.control) {
			this.formControl = this.control;
		}
		if (!this.label && !this.hideLabel) {
			this.hideLabel = true;
		}
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.control && simpleChanges.control.currentValue && !this.formControl) {
			this.attachExceptions(this.formControl = this.control);
		}
	}

	ngAfterViewInit() {
		// In normal cases where a form is not including a component with other form elements (provide-parent-form) this will do nothing.
		if (this.form && !this.formControl) {
			this.formControlChangedSubscription = this.form.form.valueChanges.subscribe((state: any) => {
				if (!this.formControl) {
					this.formControl = this.form.form.controls[this.formControlName];
					this.attachExceptions(this.formControl);
				}
			});
		} else {
			this.attachExceptions(this.formControl);
		}
	}

	ngOnDestroy() {
		if (this.formControlChangedSubscription) {
			this.formControlChangedSubscription.unsubscribe();
		}
	}

	determineInputElement() {
		this.numCheckboxes = this.inputWrapper.nativeElement.querySelectorAll('bb-check').length;
		// first element with a name attribute
		this.inputElement = this.inputWrapper.nativeElement.querySelector('[name]');

		if (!this.inputElement) {
			// we assume the inputWrapper's first "real" childnode, is our input element
			this.inputWrapper.nativeElement.childNodes.forEach((node: HTMLElement) => {
				if (!this.inputElement && node.tagName) {
					this.inputElement = node;
				}
			});
		}
	}

	actionButtonClicked() {
		this.actionButtonClick.emit();
	}

	attachExceptions(formControl: AbstractControl) {
		if (formControl) {
			if (this.formControlChangedSubscription) {
				this.formControlChangedSubscription.unsubscribe();
				this.formControlChangedSubscription = null;
			}
			formControl.statusChanges.pipe(
				debounceTime(500)
			).subscribe((change: any) => {
				this.exceptions = !this.formControl.valid && (!this.formControl.pristine || (this.form && this.form.submitted));
			}
			);
		}
	}

	onLabelClick(ev: Event) {
		if (this.inputElement) {
			switch (this.inputElement.tagName.toUpperCase()) {
				case 'BB-CHECK':
					if (this.numCheckboxes < 2) {
						this.inputElement.click();
					}
					break;
				case 'INPUT':
				case 'TEXTAREA':
					this.inputElement.focus();
					break;
				case 'MAT-SLIDE-TOGGLE':
					const el = (this.inputElement as HTMLElement).getElementsByTagName('label')[0];
					if (el) {
						el.click();
					}
					break;
			}
		}
	}
}
